import { suggestions as tweakwiseSuggestions } from "@jog/shared"
import type { SuggestionAPIResponse } from "@jog/types"
import { useRouter } from "next/router"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

export const useSuggestions = () => {
    const [searchTerm, setSearchTerm] = useState("")
    const [isSearchShown, setIsSearchShown] = useState(false)
    const [suggestions, setSuggestions] = useState<SuggestionAPIResponse | null>(null)
    const onSearch = useCallback(async () => {
        if (searchTerm.trim().length > 2) {
            await tweakwiseSuggestions(searchTerm)
                .then((res) => res.body)
                .then((res) => {
                    setSuggestions(res)
                    setIsSearchShown(true)
                })
                .catch(() => {
                    setIsSearchShown(false)
                })
        } else {
            setSuggestions(null)
            setIsSearchShown(false)
        }
    }, [searchTerm])

    const timer = useRef<NodeJS.Timeout | null>(null)
    useEffect(() => {
        if (timer.current) {
            clearTimeout(timer.current)
            timer.current = null
        }
        timer.current = setTimeout(async () => {
            await onSearch()
        }, 300)
        return () => {
            if (timer.current) clearTimeout(timer.current)
        }
    }, [onSearch])
    const router = useRouter()

    const handleSearch = useCallback(
        async (searchTerm: string) => {
            if (searchTerm) {
                setIsSearchShown(false)
                if (router.pathname === "/search") {
                    await router.replace({ pathname: "/search", query: { tn_q: searchTerm } })
                } else {
                    await router.push({ pathname: "/search", query: { tn_q: searchTerm } })
                }
            }
        },
        [router, setIsSearchShown]
    )
    return {
        isSearchShown: useMemo(
            () =>
                isSearchShown &&
                !!(
                    suggestions?.searchPhrases?.length ||
                    suggestions?.categories?.length ||
                    suggestions?.products?.items?.length
                ),
            [isSearchShown, suggestions]
        ),
        suggestions,
        searchTerm,
        setSearchTerm,
        setIsSearchShown,
        handleSearch,
    }
}
